import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { el } from "date-fns/locale";
registerLocale("el", el);

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useAxiosPrivate, {
	toastErrorUnlessHandled,
} from "@/hooks/useAxiosPrivate";
import ModulesTab from "@/components/ModulesTab";
import { getAllFeatures, type Feature } from "@/api/featuresApi";
import { clampNumberInput, lengthTypesMap, MAX_NUMBER_INPUT, normalizeNumberInputDisplay } from "@/utils";
import {
	Button,
	Divider,
	NumberInput,
	SearchSelect,
	SearchSelectItem,
	Select,
	SelectItem,
} from "@tremor/react";
import axios from "axios";
import { addDays, addMonths, addYears, format } from "date-fns";
import type React from "react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface AddSubscriptionProps {
	productId: string;
	orgId: string;
	onSubscriptionAdded: () => void;
	lastSubscriptionEndDate?: string;
	defaultFeatureKeys?: string[];
}

const AddSubscription: React.FC<AddSubscriptionProps> = ({
	productId,
	orgId,
	onSubscriptionAdded,
	lastSubscriptionEndDate,
	defaultFeatureKeys = [],
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [newSubscription, setNewSubscription] = useState({
		package_id: "",
		start_date: lastSubscriptionEndDate ?? "",
		end_date: "",
		allowed_credits: 0,
		allowed_users: 1,
	});
	const [lengthType, setLengthType] = useState("");
	const [subscriptionQuantity, setSubscriptionQuantity] = useState(1);
	const [notifyCustomer, setNotifyCustomer] = useState(true);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const [packages, setPackages] = useState<any[]>([]);
	const [subscriptions, setSubscriptions] = useState<
		{
			package_id: string;
			start_date: string;
			end_date: string;
			allowed_credits: number;
			allowed_users: number;
		}[]
	>([]);
	const [availableFeatures, setAvailableFeatures] = useState<Feature[]>([]);
	const [selectedFeatureKeys, setSelectedFeatureKeys] =
		useState<string[]>(defaultFeatureKeys);

	const axiosPrivate = useAxiosPrivate();

	useEffect(() => {
		let isMounted = true;
		const controller = new AbortController();

		getAllFeatures(controller.signal)
			.then((response) => {
				if (isMounted) {
					setAvailableFeatures(response.data.data);
				}
			})
			.catch((error) => {
				console.error("Error fetching available features:", error);
				toastErrorUnlessHandled(
					error,
					"Σφάλμα κατά τη φόρτωση των διαθέσιμων επεκτάσεων",
				);
			});

		return () => {
			isMounted = false;
			controller.abort();
		};
	}, []);

	useEffect(() => {
		if (lastSubscriptionEndDate) {
			setNewSubscription((prev) => ({ ...prev, start_date: lastSubscriptionEndDate }));
		}
	}, [lastSubscriptionEndDate]);

	useEffect(() => {
		if (isOpen) {
			setSelectedFeatureKeys(defaultFeatureKeys);
		}
	}, [defaultFeatureKeys, isOpen]);

	async function getPackagesForProduct(
		isMounted = true,
		controller = new AbortController(),
	) {
		try {
			const response = await axiosPrivate.get(
				`/auth/packages/product/${Number.parseInt(productId!)}`,
				{ signal: controller.signal },
			);
			isMounted && setPackages(response.data.packages);
		} catch (error) {
			console.error(error);
		}
	}

	useEffect(() => {
		let isMounted = true;
		const controller = new AbortController();
		getPackagesForProduct(isMounted, controller);
		return () => {
			isMounted = false;
			controller.abort();
		};
	}, []);

	useEffect(() => {
		if (newSubscription.package_id) {
			const pkg = packages?.find(
				(item) => item.id.toString() === newSubscription.package_id,
			);
			setLengthType(pkg?.details?.length_type?.[0]);
			setNewSubscription((prev) => ({ ...prev, allowed_credits: pkg?.details?.allowed_credits }));
		}
	}, [newSubscription.package_id, packages]);

	useEffect(() => {
		if (newSubscription.start_date && lengthType) {
			let newEndDate = new Date(newSubscription.start_date);
			switch (lengthType) {
				case "year":
					newEndDate = addYears(newEndDate, 1);
					break;
				case "month":
					newEndDate = addMonths(newEndDate, 1);
					break;
				case "demo":
					newEndDate = addDays(newEndDate, 15);
					break;
				case "open":
					setNewSubscription((prev) => ({ ...prev, end_date: "" }));
					return;
			}
			newEndDate = addDays(newEndDate, -1);
			setNewSubscription((prev) => ({
				...prev,
				end_date: format(newEndDate, "yyyy-MM-dd"),
			}));
		}
	}, [newSubscription.start_date, lengthType]);

	useEffect(() => {
		const newSubscriptions: typeof subscriptions = [];
		for (let i = 0; i < subscriptionQuantity; i++) {
			const subscriptionObj = {
				package_id: newSubscription.package_id,
				start_date: newSubscription.start_date,
				end_date: newSubscription.end_date,
				allowed_credits: newSubscription.allowed_credits,
				allowed_users: newSubscription.allowed_users,
			};
			if (i > 0) {
				if (lengthType === "year") {
					subscriptionObj.start_date = format(addYears(new Date(subscriptionObj.start_date), i), "yyyy-MM-dd");
					subscriptionObj.end_date = format(addYears(new Date(subscriptionObj.end_date), i), "yyyy-MM-dd");
				} else if (lengthType === "month") {
					subscriptionObj.start_date = format(addMonths(new Date(subscriptionObj.start_date), i), "yyyy-MM-dd");
					subscriptionObj.end_date = format(addMonths(new Date(subscriptionObj.end_date), i), "yyyy-MM-dd");
				}
			}
			newSubscriptions.push(subscriptionObj);
		}
		setSubscriptions(newSubscriptions);
	}, [
		subscriptionQuantity,
		newSubscription.package_id,
		newSubscription.start_date,
		newSubscription.end_date,
		newSubscription.allowed_credits,
		newSubscription.allowed_users,
		lengthType,
	]);

	const handleInputChange = (field: string, value: string | number) => {
		setNewSubscription((prev) => ({
			...prev,
			[field]:
				typeof value === "number" ? clampNumberInput(value) : value,
		}));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			const response = await axiosPrivate.post("/auth/subscriptions", {
				subscriptions,
				product_id: productId,
				org_id: orgId,
				feature_keys: selectedFeatureKeys,
				notify_customer: notifyCustomer,
			});
			toast.success("Η συνδρομή προστέθηκε με επιτυχία");
			if (response.data?.auto_paused_users_count > 0) {
				toast.warning(
					`${response.data.auto_paused_users_count} χρήστες τέθηκαν αυτόματα σε παύση λόγω του νέου ορίου.`,
				);
			}
			if (response.data?.email_warning) {
				toast.warning(response.data.email_warning);
			}
			setIsOpen(false);
			onSubscriptionAdded();
		} catch (error) {
			console.error("Error adding subscription:", error);
			if (axios.isAxiosError(error)) {
				toastErrorUnlessHandled(error, `${error.response?.data}`);
			} else if (error instanceof Error) {
				toastErrorUnlessHandled(error, `${error.message}`);
			} else {
				toastErrorUnlessHandled(
					error,
					"Προέκυψε σφάλμα κατά την προσθήκη της συνδρομής.",
				);
			}
		}
	};

	return (
		<Dialog
			open={isOpen}
			onOpenChange={(open) => {
				setIsOpen(open);
				if (!open) {
					setNotifyCustomer(true);
				}
			}}
		>
			<DialogTrigger asChild>
				<Button onClick={() => setIsOpen(true)}>Προσθήκη Νέας Συνδρομής</Button>
			</DialogTrigger>
			<DialogContent
				className="w-[600px] h-[700px] max-w-[95vw] max-h-[95vh] flex flex-col overflow-hidden"
				onPointerDownOutside={(e) => e.preventDefault()}
			>
				<DialogHeader>
					<DialogTitle>Προσθήκη Νέας Συνδρομής</DialogTitle>
				</DialogHeader>
				<form
					onSubmit={handleSubmit}
					className="flex flex-col flex-1 min-h-0 gap-4"
				>
					<Tabs defaultValue="sub" className="flex flex-col flex-1 min-h-0">
						<TabsList className="w-full shrink-0">
							<TabsTrigger value="sub" className="flex-1">
								Στοιχεία Συνδρομής
							</TabsTrigger>
							<TabsTrigger value="ext" className="flex-1">
								Επεκτάσεις
							</TabsTrigger>
						</TabsList>

						<TabsContent value="sub" className="space-y-4 mt-4 flex-1 overflow-y-auto pr-1">
						<div className="grid grid-cols-2 gap-4">
							<div>
								<label
										htmlFor="subscriptionType"
										className="block text-sm font-medium text-gray-700"
									>
										Πακέτο
									</label>
									<SearchSelect
										placeholder="Επιλέξτε"
										value={newSubscription.package_id}
										onValueChange={(value) => handleInputChange("package_id", value)}
									>
										{packages?.map(
											(item) =>
												item && (
													<SearchSelectItem key={item?.id} value={item?.id?.toString()}>
														{item?.name}
													</SearchSelectItem>
												),
										)}
									</SearchSelect>
								</div>
								<div>
									<label
										htmlFor="subscriptionLength"
										className="block text-sm font-medium text-gray-700"
									>
										Διάρκεια Συνδρομής
									</label>
									<Select
										id="subscriptionLength"
										value={lengthType}
										onValueChange={setLengthType}
										enableClear={false}
										placeholder="Επιλέξτε"
									>
										{packages
											?.find((item) => item.id.toString() === newSubscription.package_id)
											?.details?.length_type?.map((k) => (
												<SelectItem key={k} value={k}>
													{lengthTypesMap[k]}
												</SelectItem>
											))}
									</Select>
								</div>
							</div>

							<div className="grid grid-cols-2 gap-4">
								<div>
									<label
										htmlFor="allowedCredits"
										className="block text-sm font-medium text-gray-700"
									>
										Επιτρεπόμενα Credits
									</label>
									<NumberInput
										id="allowedCredits"
										placeholder="Αριθμός Credits"
										value={newSubscription.allowed_credits}
										onValueChange={(value) => handleInputChange("allowed_credits", value)}
									onChange={(event) => {
										event.target.value = normalizeNumberInputDisplay(event.target.value);
									}}
									min={0}
									max={MAX_NUMBER_INPUT}
									/>
								</div>
								<div>
									<label
										htmlFor="allowedUsers"
										className="block text-sm font-medium text-gray-700"
									>
										Επιτρεπόμενοι Χρήστες
									</label>
									<NumberInput
										id="allowedUsers"
										placeholder="Αριθμός Χρηστών"
										value={newSubscription.allowed_users}
										onValueChange={(value) =>
											handleInputChange(
												"allowed_users",
												clampNumberInput(value, { min: 1 }),
											)
										}
										onChange={(event) => {
											event.target.value = normalizeNumberInputDisplay(
												event.target.value,
												{ min: 1 },
											);
										}}
										min={1}
										max={MAX_NUMBER_INPUT}
									/>
								</div>
							</div>

							<div className="grid grid-cols-2 gap-4">
								<div>
									<label
										htmlFor="startDate"
										className="block text-sm font-medium text-gray-700"
									>
										Ημερομηνία Έναρξης
									</label>
									<DatePicker
										selected={newSubscription.start_date ? new Date(newSubscription.start_date) : undefined}
										onChange={(date) => {
											date?.setHours(10);
											handleInputChange("start_date", date ? date.toISOString().split("T")[0] : "");
										}}
										locale={el}
										dateFormat="dd/MM/yyyy"
										placeholderText="Επιλέξτε Ημερομηνία"
										showYearDropdown
										yearDropdownItemNumber={40}
										scrollableYearDropdown
										className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									/>
								</div>
								{newSubscription.end_date && (
									<div>
										<label
											htmlFor="endDate"
											className="block text-sm font-medium text-gray-700"
										>
											Ημερομηνία Λήξης
										</label>
										<DatePicker
											selected={new Date(newSubscription.end_date)}
											onChange={(date) => handleInputChange("end_date", date ? date.toISOString().split("T")[0] : "")}
											locale={el}
											dateFormat="dd/MM/yyyy"
											placeholderText="Επιλέξτε Ημερομηνία"
											showYearDropdown
											yearDropdownItemNumber={40}
											scrollableYearDropdown
											readOnly
											className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
										/>
									</div>
								)}
							</div>

							{newSubscription.end_date && (
								<div>
									<label
										htmlFor="subscriptionQuantity"
										className="block text-sm font-medium text-gray-700"
									>
										Ποσότητα Συνδρομών
									</label>
									<NumberInput
										id="subscriptionQuantity"
										placeholder="Ποσότητα"
										value={subscriptionQuantity}
										onValueChange={(value) =>
											setSubscriptionQuantity(
												clampNumberInput(value, { min: 1 }),
											)
										}
										min={1}
										max={MAX_NUMBER_INPUT}
									/>
								</div>
							)}
							<div className="flex items-center space-x-2">
								<Checkbox
									id="notifyCustomer"
									checked={notifyCustomer}
									onCheckedChange={(checked) =>
										setNotifyCustomer(checked === true)
									}
								/>
								<label
									htmlFor="notifyCustomer"
									className="text-sm font-medium text-gray-700"
								>
									Ενημέρωση πελάτη
								</label>
							</div>
						</TabsContent>

						<TabsContent value="ext" className="mt-4 flex-1 overflow-y-auto pr-1">
							<ModulesTab
								modules={availableFeatures}
								selectedKeys={selectedFeatureKeys}
								setSelectedKeys={setSelectedFeatureKeys}
								emptyText="Δεν υπάρχουν διαθέσιμες επεκτάσεις"
							/>
						</TabsContent>
					</Tabs>

					<Divider className="shrink-0" />

					<div className="flex justify-center space-x-4 shrink-0">
						<Button type="submit" color="emerald" className="w-2/3">
							Προσθήκη Συνδρομής
						</Button>
						<Button
							type="button"
							color="red"
							onClick={() => setIsOpen(false)}
							className="w-1/3"
						>
							Ακύρωση
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default AddSubscription;
